import TextEditorCustomize from '@/components/TextEditorCustomize'
import {
    DatacentralHookForm,
    type DatacentralHookFormDataItem,
} from '@/components/ui/form-field'
import { AsyncFilterSelect } from '@/components/ui/filters/async-filter-select'
import { DEPARTMENT_IDS } from '@/constants/departments'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import useQueryClientCallback from '@/hooks/useQueryClientCallback'
import useUploadFile from '@/hooks/useUploadFile'
import {
    getEmployeesDefaultOptions,
    getEmployeesOptionsPaginated,
} from '@/modules/Employee/services'
import { STORGE_PREFIXS } from '@/modules/Contract/modules/Process/constants'
import { ContractProcessCreateParams } from '@/modules/Contract/modules/Process/types'
import { createSubcontractProcess } from '@/modules/Contract/modules/Subcontract/modules/SubcontractProcess/services'
import { Attachment } from '@/modules/Upload/types'
import { ModalProps } from '@/types/shared'
import { formatDateWithFormatter, renderFileItem } from '@/utils/function'
import { yupResolver } from '@hookform/resolvers/yup'
import dayjs from 'dayjs'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Dialog, FormItem } from 'vnetwork-ui'
import { schemaCreateContractProcessModal } from './schema'
import { QUERY_KEY_COMBINATIONS } from '@/constants/reactQueryKeys'

export interface CreateSubcontractProcessFormValues {
    subcontract: ContractProcessCreateParams
    attachments?: File[]
}

export interface CreateContractProcessModalProps extends ModalProps {
    contractID: number
    subcontractID: number
}

const CreateContractProcessModal = ({
    onClose,
    open,
    subcontractID,
    contractID,
}: CreateContractProcessModalProps) => {
    const { t } = useTranslation()
    const { refetch } = useQueryClientCallback({
        reactQueryKeys: [
            'contracts.customers.detail',
            'subcontracts.processes',
        ],
    })

    const formControl = useForm<CreateSubcontractProcessFormValues>({
        defaultValues: {
            subcontract: {
                contract_process_title: '',
                contract_process_content: '',
                contract_process_problem: '',
                contract_process_assignees:
                    import.meta.env.APP_PROCESS_ASSIGNEE_IDS?.split(',')?.map(
                        Number
                    ) || [],
                contract_process_deadline: dayjs(),
                contract_process_priority: 'medium',
            },
            attachments: [],
        },
        resolver: yupResolver(schemaCreateContractProcessModal as any),
    })

    const { mutateAsync, isPending } = useMutationWithHandler(
        {
            mutationFn: createSubcontractProcess,
        },
        {
            successMessage: t('add-new-process-success'),
            reactQueryKeys: QUERY_KEY_COMBINATIONS.SUBCONTRACT_WITH_PROCESS,
            onSuccess: () => {
                onClose()
                refetch()
            },
        }
    )

    const { isUploading, uploadAsync } = useUploadFile({
        prefix: STORGE_PREFIXS.SUBCONTRACT_PROCESS,
    })

    const handleCreate = async (values: CreateSubcontractProcessFormValues) => {
        let attachments: Attachment[] = []
        if (values?.attachments) {
            const attachmentUploadeds = await uploadAsync(values?.attachments)

            attachments = attachmentUploadeds
                ?.filter((attachment) => attachment.status === 'fulfilled')
                .map((attachment) => attachment.value) as Attachment[]
        }

        mutateAsync({
            contractID,
            subcontractID,
            data: {
                ...values.subcontract,
                contract_process_deadline: formatDateWithFormatter(
                    values?.subcontract?.contract_process_deadline,
                    'YYYY-MM-DDTHH:mm:ssZ'
                ),
            },
            attachments,
        })
    }

    const isLoading = isUploading || isPending

    const formData: DatacentralHookFormDataItem<CreateSubcontractProcessFormValues>[] =
        [
            {
                formItemProps: {
                    name: 'subcontract.contract_process_title',
                    type: 'textfield',
                    label: t('contract_process_title'),
                    placeholder: t('contract_process_title'),
                },
                colSpan: 4,
            },
            {
                renderEditComponent() {
                    return (
                        <TextEditorCustomize
                            label={t('contract_process_content')}
                            value={{
                                text: formControl.getValues(
                                    'subcontract.contract_process_content'
                                ),
                                attachments: [],
                            }}
                            placeholder={t('contract_process_content')}
                            onChange={(value) => {
                                formControl.setValue(
                                    'subcontract.contract_process_content',
                                    value.text
                                )
                            }}
                        />
                    )
                },
                colSpan: 4,
            },
            {
                formItemProps: {
                    name: 'subcontract.contract_process_problem',
                    type: 'textarea',
                    label: t('contract_process_problem'),
                    placeholder: t('contract_process_problem'),
                },
                colSpan: 4,
            },
            {
                renderEditComponent(control) {
                    return (
                        <AsyncFilterSelect
                            control={control}
                            name={
                                'subcontract.contract_process_assignees' as any
                            }
                            label={t('contract_process_assignees')}
                            multiple
                            asyncOptions={(query, page) =>
                                getEmployeesOptionsPaginated(
                                    query,
                                    [
                                        DEPARTMENT_IDS.FINANCE,
                                        DEPARTMENT_IDS.BOARD_OF_DIRECTORS,
                                    ],
                                    'active',
                                    page
                                )
                            }
                            getDefaultOptions={getEmployeesDefaultOptions}
                        />
                    )
                },
                colSpan: 4,
            },
            {
                formItemProps: {
                    name: 'subcontract.contract_process_deadline',
                    type: 'datepicker',
                    label: t('contract_process_deadline'),
                    placeholder: t('contract_process_deadline'),
                },
                colSpan: 4,
            },
            {
                renderEditComponent(control) {
                    return (
                        <FormItem
                            control={control}
                            name="attachments"
                            type="files"
                            renderFileItem={renderFileItem}
                            label={t('attachments')}
                            multiple={true}
                        />
                    )
                },
                colSpan: 4,
            },
        ]

    return (
        <Dialog
            title={t('create-subcontract-process')}
            onClose={onClose}
            open={open}
        >
            <DatacentralHookForm
                formControl={formControl}
                className="mt-2"
                onSubmit={handleCreate}
                data={formData}
                actions={[
                    {
                        title: t('cancel-operation'),
                        type: 'button',
                        variant: 'outline',
                        handler: onClose,
                        disabled: isLoading,
                    },
                    {
                        title: t('create'),
                        type: 'submit',
                        variant: 'default',
                        loading: isLoading,
                        disabled: isLoading,
                    },
                ]}
            />
        </Dialog>
    )
}

export default CreateContractProcessModal
